import React, { useRef } from 'react';
import { Box, FormLabel, TextField } from '@material-ui/core';
import { BodyMassIndexAnswer } from '../../types/Answer';
import { BodyMassIndexQuestion } from '../../types/Question';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(1),
    '& .MuiTextField-root': {
      flex: 1,
    },
  },
}));

type BodyMassIndexInputProps = BodyMassIndexQuestion['props'] & {
  answer?: BodyMassIndexAnswer;
  onChange?: (newAnswer: BodyMassIndexAnswer) => void;
};

const BodyMassIndexInput = ({ answer, onChange }: BodyMassIndexInputProps) => {
  const classes = useStyles();
  const heightInRef = useRef<HTMLInputElement | null>(null);
  const weightLbRef = useRef<HTMLInputElement | null>(null);

  const heightFt = answer?.bodyMassIndex?.heightFt || '';
  const heightIn = answer?.bodyMassIndex?.heightIn || '';
  const weightLb = answer?.bodyMassIndex?.weightLb || '';

  const calculateBMI = () => {
    // Only show a BMI value once all inputs are provided
    if (!heightFt || !heightIn || !weightLb) {
      return '';
    }

    const ft = parseFloat(heightFt) || 0;
    const inches = parseFloat(heightIn) || 0;
    const lbs = parseFloat(weightLb) || 0;

    const heightInMeters = ft * 0.3048 + inches * 0.0254;
    const weightInKg = lbs * 0.453592;
    const bmi = weightInKg / Math.pow(heightInMeters, 2);

    return isNaN(bmi) || !isFinite(bmi) ? '' : bmi.toFixed(1);
  };

  const handleChange =
    (field: 'heightFt' | 'heightIn' | 'weightLb', min?: number, max?: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value.replace(/\D/g, '');

      // Validate range if provided
      if (min !== undefined && max !== undefined && nextValue !== '') {
        const numValue = parseInt(nextValue, 10);
        if (numValue < min || numValue >= max) {
          return;
        }
      }

      // Auto-advance focus
      if (field === 'heightFt' && nextValue.length >= 1 && heightInRef.current) {
        heightInRef.current.focus();
        heightInRef.current.setSelectionRange(0, heightInRef.current.value.length);
      } else if (field === 'heightIn' && nextValue.length >= 2 && weightLbRef.current) {
        weightLbRef.current.focus();
        weightLbRef.current.setSelectionRange(0, weightLbRef.current.value.length);
      }

      // Update answer
      const newBodyMassIndex = {
        ...answer?.bodyMassIndex,
        [field]: nextValue,
      };

      const hasAllFields =
        (field === 'heightFt' ? nextValue : newBodyMassIndex.heightFt ?? '')?.length >= 1 &&
        (field === 'heightIn' ? nextValue : newBodyMassIndex.heightIn ?? '')?.length >= 1 &&
        (field === 'weightLb' ? nextValue : newBodyMassIndex.weightLb ?? '')?.length >= 1;

      onChange?.({
        bodyMassIndex: newBodyMassIndex,
        progress: hasAllFields ? 1 : 0,
      });
    };

  return (
    <Box mb={2} style={{ maxWidth: '400px' }}>
      <Box mb={1}>
        <FormLabel>Height</FormLabel>
        <div className={classes.root}>
          <TextField
            variant="filled"
            label="Feet"
            type="text"
            value={heightFt}
            onChange={handleChange('heightFt')}
            inputProps={{
              maxLength: 1,
            }}
          />
          <TextField
            variant="filled"
            type="text"
            label="Inches"
            inputRef={heightInRef}
            value={heightIn}
            onChange={handleChange('heightIn', 0, 12)}
            inputProps={{
              maxLength: 2,
            }}
          />
        </div>
      </Box>
      <Box mb={1}>
        <FormLabel>Weight</FormLabel>
        <TextField
          type="text"
          variant="filled"
          label="Pounds"
          inputRef={weightLbRef}
          value={weightLb}
          onChange={handleChange('weightLb')}
          inputProps={{
            maxLength: 3,
          }}
          fullWidth
        />
      </Box>
      <Box mb={1}>
        <FormLabel>BMI</FormLabel>
        <TextField
          type="text"
          variant="filled"
          label="BMI"
          value={calculateBMI()}
          InputProps={{ readOnly: true }}
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default BodyMassIndexInput;
